/**
 * Estado do formulário de automação do Instagram e as conversões de/para o
 * shape do servidor. Fica fora da página para a lista (que precisa de
 * `deLinha` para o botão "duplicar") e o editor não divergirem.
 */

import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../../server/routers";
import { normalizarListaDeChaves } from "@shared/instagram-keywords";
import type { InstagramAutomationTemplate } from "@shared/instagram-automation-templates";

export type Alvo = "post" | "any" | "next_reel";

export type AutomationForm = {
  name: string;
  isActive: boolean;
  /** Conta em que roda. Null só serve quando o workspace tem uma conta. */
  instagramAccountId: number | null;
  alvo: Alvo;
  mediaId: string;
  mediaPermalink: string | null;
  mediaThumb: string | null;
  keywordsTexto: string;
  matchAnyWord: boolean;
  wholeWordMatch: boolean;
  dmTriggerEnabled: boolean;
  dmMessage: string;
  linkButtonLabel: string;
  link1: string;
  link2: string;
  link2Label: string;
  openingDmEnabled: boolean;
  openingDmMessage: string;
  openingDmButtonLabel: string;
  requireFollow: boolean;
  followPromptMessage: string;
  followPromptButtonLabel: string;
  followUpEnabled: boolean;
  followUpMessage: string;
  followUpDelayMinutes: number;
  publicReplyEnabled: boolean;
  publicReplyTexto: string;
};

export const FORM_VAZIO: AutomationForm = {
  name: "",
  isActive: true,
  instagramAccountId: null,
  alvo: "post",
  mediaId: "",
  mediaPermalink: null,
  mediaThumb: null,
  keywordsTexto: "",
  matchAnyWord: false,
  wholeWordMatch: true,
  dmTriggerEnabled: false,
  dmMessage: "Oi {nome}! Aqui está o link que você pediu 👇",
  linkButtonLabel: "Abrir link",
  link1: "",
  link2: "",
  link2Label: "",
  openingDmEnabled: false,
  openingDmMessage: "Oi {nome}! Toque no botão abaixo para receber o link 👇",
  openingDmButtonLabel: "Quero o link",
  requireFollow: false,
  followPromptMessage: "",
  followPromptButtonLabel: "",
  followUpEnabled: false,
  followUpMessage: "",
  followUpDelayMinutes: 10,
  publicReplyEnabled: false,
  publicReplyTexto: "Te mandei no privado! 📩\nAcabei de enviar na sua DM 😉",
};

export const TEXTO_PADRAO_SEGUIR =
  "Antes de te mandar o link: segue a gente por aqui? Toque no botão assim que estiver seguindo que eu envio na hora 😉";
export const ROTULO_PADRAO_SEGUIR = "Já estou seguindo";

export type AutomationRow = inferRouterOutputs<AppRouter>["instagram"]["automations"]["list"][number];
export type AutomationDetail = inferRouterOutputs<AppRouter>["instagram"]["automations"]["get"];

export function deLinha(a: AutomationRow | AutomationDetail): AutomationForm {
  const [l1, l2] = a.trackedLinks;
  return {
    name: a.name,
    isActive: a.isActive,
    instagramAccountId: a.instagramAccountId,
    alvo: a.pendingNextReel ? "next_reel" : a.matchAnyPost ? "any" : "post",
    mediaId: a.mediaId ?? "",
    mediaPermalink: a.mediaPermalink,
    mediaThumb: null,
    keywordsTexto: a.keywords.join(", "),
    matchAnyWord: a.matchAnyWord,
    wholeWordMatch: a.wholeWordMatch,
    dmTriggerEnabled: a.dmTriggerEnabled,
    dmMessage: a.dmMessage,
    linkButtonLabel: a.linkButtonLabel ?? "",
    link1: l1?.destinationUrl ?? "",
    link2: l2?.destinationUrl ?? "",
    link2Label: l2?.label ?? "",
    openingDmEnabled: a.openingDmEnabled,
    openingDmMessage: a.openingDmMessage ?? FORM_VAZIO.openingDmMessage,
    openingDmButtonLabel: a.openingDmButtonLabel ?? FORM_VAZIO.openingDmButtonLabel,
    requireFollow: a.requireFollow,
    followPromptMessage: a.followPromptMessage ?? "",
    followPromptButtonLabel: a.followPromptButtonLabel ?? "",
    followUpEnabled: a.followUpEnabled,
    followUpMessage: a.followUpMessage ?? "",
    followUpDelayMinutes: a.followUpDelayMinutes,
    publicReplyEnabled: a.publicReplyEnabled,
    publicReplyTexto: a.publicReplyMessages.join("\n"),
  };
}

export function deModelo(t: InstagramAutomationTemplate): AutomationForm {
  return {
    ...FORM_VAZIO,
    name: t.title,
    keywordsTexto: t.keywords.join(", "),
    dmMessage: t.dmMessage,
    linkButtonLabel: t.linkButtonLabel,
    publicReplyEnabled: true,
    publicReplyTexto: t.publicReplyMessages.join("\n"),
  };
}

export function paraEnvio(f: AutomationForm) {
  const links: Array<{ destinationUrl: string; label?: string | null }> = [];
  if (f.link1.trim()) links.push({ destinationUrl: f.link1.trim(), label: null });
  if (f.link2.trim()) links.push({ destinationUrl: f.link2.trim(), label: f.link2Label.trim() || null });
  return {
    name: f.name,
    isActive: f.isActive,
    instagramAccountId: f.instagramAccountId,
    mediaId: f.alvo === "post" && f.mediaId.trim() ? f.mediaId.trim() : null,
    mediaPermalink: f.alvo === "post" ? f.mediaPermalink : null,
    matchAnyPost: f.alvo === "any",
    pendingNextReel: f.alvo === "next_reel",
    keywords: normalizarListaDeChaves(f.keywordsTexto),
    matchAnyWord: f.matchAnyWord,
    wholeWordMatch: f.wholeWordMatch,
    dmTriggerEnabled: f.dmTriggerEnabled,
    dmMessage: f.dmMessage,
    linkButtonLabel: f.linkButtonLabel.trim() || null,
    links,
    openingDmEnabled: f.openingDmEnabled,
    openingDmMessage: f.openingDmEnabled ? f.openingDmMessage : null,
    openingDmButtonLabel: f.openingDmEnabled ? f.openingDmButtonLabel : null,
    requireFollow: f.requireFollow,
    followPromptMessage: f.requireFollow ? f.followPromptMessage || null : null,
    followPromptButtonLabel: f.requireFollow ? f.followPromptButtonLabel || null : null,
    followUpEnabled: f.followUpEnabled,
    followUpMessage: f.followUpEnabled ? f.followUpMessage : null,
    followUpDelayMinutes: f.followUpDelayMinutes,
    publicReplyEnabled: f.publicReplyEnabled,
    publicReplyMessages: f.publicReplyEnabled
      ? f.publicReplyTexto.split("\n").map((s) => s.trim()).filter(Boolean)
      : [],
  };
}

/** Mesma substituição que o servidor faz, para a prévia não mentir. */
export function renderizarPrevia(texto: string, nome: string | null): string {
  const re = /\s?\{(?:nome|username|name)\}/gi;
  const semLink = texto.replace(/\s*\{link\}\s*/gi, " ");
  return (nome ? semLink.replace(re, (m) => (m.startsWith(" ") ? ` ${nome}` : nome)) : semLink.replace(re, ""))
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}
